// @ts-check
/**
 * attribution.js — captura y derivación de UTMs + click IDs para marketing attribution.
 *
 * Isomorphic-safe: funciones puras corren igual en client y server. Las que tocan
 * window/localStorage chequean typeof window antes (no explotan en SSR).
 *
 * Flujo:
 *   1. Usuario llega de un Reel/anuncio → URL con ?utm_source=instagram&utm_medium=reel...
 *   2. <AttributionTracker /> (client component) llama captureAttributionFromURL()
 *      al mount → guarda first-touch en localStorage
 *   3. Si el usuario navega y vuelve después, no se sobreescribe (first-touch wins)
 *   4. Al signup, el form lee getAttributionForSignup() y lo manda al API
 *   5. El API hace UPDATE de profiles con utm_* + attribution_raw
 *   6. Al crear Stripe checkout, baseMetadata incluye los utm_* del profile
 *      → cada Stripe customer queda con el origen para attribution multi-touch
 *
 * Convenciones del proyecto:
 *   - First-touch wins (no last-click) porque el plan paga ocurre típicamente
 *     varios días después del primer contacto
 *   - TTL de 30 días — pasado ese tiempo, attribution expira y se re-captura
 *   - Click IDs (fbclid, gclid, ttclid) siempre se guardan para Conversion API
 *     server-side (CAPI / Events API) en el futuro
 */

const STORAGE_KEY = 'dv_attribution_v1';
const TTL_DAYS = 30;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

/**
 * Lee UTMs + click IDs + contexto de una URL.
 * Pura — no toca window/localStorage. Sirve tanto en client como server.
 *
 * @param {string} urlString
 * @param {string|null} [referrer]
 * @returns {any}
 */
export function parseAttributionFromURL(urlString, referrer = null) {
  if (!urlString) return null;
  let url;
  try {
    url = new URL(urlString);
  } catch {
    return null;
  }
  const params = url.searchParams;

  // Programa de referidos (2026-05-30): si la URL trae ?ref=ABC12345 capturamos
  // el código. Lo normalizamos uppercase + sin espacios. Validación de longitud
  // (8 chars hex) se hace server-side en /api/auth/signup antes de buscar el referrer.
  const rawRef = params.get('ref');
  const refCode = rawRef ? String(rawRef).trim().toUpperCase().slice(0, 16) : null;

  const data = {
    // UTM standard
    utm_source: params.get('utm_source') || null,
    utm_medium: params.get('utm_medium') || null,
    utm_campaign: params.get('utm_campaign') || null,
    utm_content: params.get('utm_content') || null,
    utm_term: params.get('utm_term') || null,
    // Click IDs (críticos para Conversion API server-side)
    fbclid: params.get('fbclid') || null,    // Meta (Instagram/Facebook)
    gclid: params.get('gclid') || null,      // Google Ads
    ttclid: params.get('ttclid') || null,    // TikTok Ads
    msclkid: params.get('msclkid') || null,  // Microsoft Ads
    // Programa referidos
    ref_code: refCode,
    // Contexto
    landing_page: url.pathname + (url.search || ''),
    referrer_url: referrer || null,
  };

  // Si NINGÚN UTM, click ID, ref code, ni referrer relevante → null
  const hasSignal = data.utm_source || data.utm_medium || data.utm_campaign ||
    data.fbclid || data.gclid || data.ttclid || data.msclkid || data.ref_code;
  if (!hasSignal && !data.referrer_url) return null;

  return data;
}

/**
 * Deriva el canal de signup a partir de los UTMs / click IDs.
 * Pura — sirve client y server.
 *
 * Heurística:
 *   - Click ID de Meta o utm_source=instagram/facebook → meta_ads
 *   - Click ID de Google o utm_source=google → google_ads
 *   - Click ID de TikTok o utm_source=tiktok → tiktok_ads
 *   - utm_medium social orgánico (bio, reel, story, post) → social_organic
 *   - utm_medium=email → email
 *   - utm_medium=referral → referral
 *   - hay referrer pero no UTMs → referral_web
 *   - nada → direct
 */
/** @param {any} data @returns {string} */
export function deriveSignupSource(data) {
  if (!data) return 'direct';

  const source = String(data.utm_source || '').toLowerCase();
  const medium = String(data.utm_medium || '').toLowerCase();

  // Paid ads (click IDs son la señal más confiable)
  if (data.fbclid || source === 'instagram' || source === 'facebook' || source === 'meta') {
    // medium=cpc indica paid; sin medium asumimos paid si hay fbclid
    if (data.fbclid || medium === 'cpc' || medium === 'paid' || medium === 'ad' || medium === 'ads') {
      return 'meta_ads';
    }
    // utm_source=instagram con medium orgánico (bio, reel, story) → social_organic
    return 'social_organic';
  }
  if (data.gclid || source === 'google') {
    if (data.gclid || medium === 'cpc' || medium === 'paid' || medium === 'ad' || medium === 'ads') {
      return 'google_ads';
    }
    return 'search_organic';
  }
  if (data.ttclid || source === 'tiktok') {
    if (data.ttclid || medium === 'cpc' || medium === 'paid' || medium === 'ad' || medium === 'ads') {
      return 'tiktok_ads';
    }
    return 'social_organic';
  }
  if (source === 'youtube') {
    return medium === 'cpc' || medium === 'paid' || medium === 'ads' ? 'youtube_ads' : 'social_organic';
  }

  // Orgánico social (Reels, Stories, posts sin promoción)
  if (medium === 'bio' || medium === 'reel' || medium === 'story' || medium === 'post' || medium === 'social') {
    return 'social_organic';
  }

  // Otros canales
  if (medium === 'email' || source === 'email' || source === 'newsletter') return 'email';
  if (medium === 'referral' || source === 'referral') return 'referral';

  // Tiene referrer pero ningún UTM → llegó de algún link en otra web
  if (data.referrer_url) {
    // Ignorar referrers internos del propio dominio
    try {
      const refHost = new URL(data.referrer_url).hostname;
      if (!refHost.includes('drvictorlegal.com')) return 'referral_web';
    } catch { /* ignore */ }
  }

  return 'direct';
}

/**
 * Lo guardado en localStorage (con TTL check). Solo client.
 * @returns {any}
 */
export function getStoredAttribution() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // TTL check
    if (parsed?.captured_at) {
      const age = Date.now() - new Date(parsed.captured_at).getTime();
      if (age > TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Captura la attribution actual desde window.location y la guarda.
 * Solo client. Política first-touch wins: si ya hay attribution válida con
 * señal (no solo referrer), no la sobrescribe — pero la guarda como last_touch
 * para multi-touch attribution futura.
 *
 * @returns {any}
 */
export function captureAttributionFromURL() {
  if (typeof window === 'undefined') return null;

  const fresh = parseAttributionFromURL(
    window.location.href,
    typeof document !== 'undefined' ? document.referrer || null : null
  );

  // No hay señal en esta URL → devolver lo que haya
  if (!fresh) return getStoredAttribution();

  const existing = getStoredAttribution();

  // First touch wins: si ya hay attribution con UTMs reales, mantenemos
  // ese first_touch pero actualizamos last_touch para análisis multi-touch.
  if (existing && (existing.utm_source || existing.fbclid || existing.gclid || existing.ttclid)) {
    const updated = {
      ...existing,
      last_touch: { ...fresh, captured_at: new Date().toISOString() },
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch { /* localStorage lleno o disabled */ }
    return updated;
  }

  // Primera vez (o la anterior solo tenía referrer): guardar como first_touch
  const captured = {
    ...fresh,
    captured_at: new Date().toISOString(),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    first_touch: { ...fresh, captured_at: new Date().toISOString() },
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
  } catch { /* localStorage lleno o disabled */ }
  return captured;
}

/**
 * Devuelve el payload listo para mandar al API de signup (o de billing checkout).
 * Solo client. Estructura compatible con las columnas de la tabla profiles.
 *
 * Si no hay attribution capturada → devuelve null (el API guarda signup_source='direct'
 * por default del schema).
 */
/** @returns {any} */
export function getAttributionForSignup() {
  if (typeof window === 'undefined') return null;
  const stored = getStoredAttribution() || captureAttributionFromURL();
  if (!stored) return null;

  return {
    utm_source: stored.utm_source || null,
    utm_medium: stored.utm_medium || null,
    utm_campaign: stored.utm_campaign || null,
    utm_content: stored.utm_content || null,
    utm_term: stored.utm_term || null,
    landing_page: stored.landing_page || null,
    referrer_url: stored.referrer_url || null,
    first_touch_at: stored.captured_at || null,
    signup_source: deriveSignupSource(stored),
    // El raw completo va a la columna jsonb para auditoría y multi-touch attribution
    attribution_raw: stored,
  };
}

/**
 * Server-side: dado un payload de attribution del request body, retorna un
 * objeto seguro para hacer UPDATE en profiles. Sanitiza tipos y trunca strings
 * largos (defensa contra payloads adversariales).
 *
 * Cualquier campo inválido se setea a null en vez de explotar.
 */
/** @param {any} payload @returns {any} */
export function normalizeAttributionForDB(payload) {
  if (!payload || typeof payload !== 'object') return null;

  const trunc = (/** @type {any} */ val, max = 500) => {
    if (val === null || val === undefined) return null;
    const s = String(val);
    return s.length > max ? s.slice(0, max) : s;
  };

  // signup_source debe ser uno de los valores enumerados (whitelist)
  const VALID_SOURCES = new Set([
    'direct', 'meta_ads', 'google_ads', 'tiktok_ads', 'youtube_ads',
    'search_organic', 'social_organic', 'email', 'referral',
    'referral_web',
  ]);
  const rawSource = String(payload.signup_source || 'direct').toLowerCase();
  const signup_source = VALID_SOURCES.has(rawSource) ? rawSource : 'direct';

  // first_touch_at debe ser una fecha válida — sino null
  let first_touch_at = null;
  if (payload.first_touch_at) {
    const d = new Date(payload.first_touch_at);
    if (!isNaN(d.getTime())) first_touch_at = d.toISOString();
  }

  // attribution_raw debe ser un objeto — limitamos tamaño para no explotar jsonb
  let attribution_raw = {};
  if (payload.attribution_raw && typeof payload.attribution_raw === 'object') {
    try {
      const serialized = JSON.stringify(payload.attribution_raw);
      // Cap a 8 KB para evitar abuse (payloads gigantes que llenen jsonb)
      if (serialized.length <= 8192) {
        attribution_raw = payload.attribution_raw;
      } else {
        // Truncamos manteniendo solo los campos críticos
        attribution_raw = {
          utm_source: payload.attribution_raw.utm_source,
          utm_medium: payload.attribution_raw.utm_medium,
          utm_campaign: payload.attribution_raw.utm_campaign,
          utm_content: payload.attribution_raw.utm_content,
          utm_term: payload.attribution_raw.utm_term,
          fbclid: payload.attribution_raw.fbclid,
          gclid: payload.attribution_raw.gclid,
          ttclid: payload.attribution_raw.ttclid,
          captured_at: payload.attribution_raw.captured_at,
          _truncated: true,
        };
      }
    } catch { /* leave empty */ }
  }

  return {
    utm_source: trunc(payload.utm_source),
    utm_medium: trunc(payload.utm_medium),
    utm_campaign: trunc(payload.utm_campaign),
    utm_content: trunc(payload.utm_content),
    utm_term: trunc(payload.utm_term),
    landing_page: trunc(payload.landing_page, 1000),
    referrer_url: trunc(payload.referrer_url, 1000),
    signup_source,
    first_touch_at,
    attribution_raw,
  };
}
